"use client";

import * as React from "react";
import { Clock, MonitorPlay, CreditCard, MessageCircle, CircleCheck, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const courseModules = [{
  title: "Módulo 1: Electrotecnia y Magnitudes Eléctricas",
  content: "Estudio profundo de tensión, intensidad, resistencia y potencia. Análisis de circuitos de corriente continua y alterna, leyes fundamentales y resolución de mallas eléctricas."
}, {
  title: "Módulo 2: Instalaciones Eléctricas en Inmuebles",
  content: "Diseño y montaje de canalizaciones, conductores y cajas. Instalación de dispositivos de maniobra, tomacorrientes y circuitos de iluminación según requerimientos técnicos."
}, {
  title: "Módulo 3: Protecciones y Tableros Eléctricos",
  content: "Dimensionamiento e instalación de interruptores termomagnéticos y diferenciales. Sistemas de puesta a tierra y montaje de tableros generales y de distribución."
}, {
  title: "Módulo 4: Mediciones y Diagnóstico de Fallas",
  content: "Uso profesional de multímetro, pinza amperométrica y telurímetro. Localización de cortocircuitos, fugas a tierra y sobrecargas, junto con protocolos de mantenimiento."
}, {
  title: "Módulo 5: Normativa UNIT y Seguridad Eléctrica",
  content: "Aplicación del reglamento de baja tensión vigente en Uruguay. Prevención de riesgos laborales, uso de equipo de protección personal (EPP) y seguridad en obra."
}];
export default function CourseDetails() {
  const phoneNumber = "59892336444";
  const whatsappMessage = encodeURIComponent("Hola, quiero información sobre el curso de Técnico electricista.");
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${whatsappMessage}`;
  return <section id="detalles-del-curso" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-8">
          
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
                Programa del Curso
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Nuestro curso de Técnico Electricista está diseñado para brindarte todas las herramientas teóricas y prácticas necesarias para insertarte rápidamente en el mercado laboral. Aprenderás desde los conceptos más básicos hasta la ejecución de instalaciones complejas, siempre bajo las normativas de seguridad vigentes.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 border-b border-border pb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold text-foreground">Temario y Módulos</h3>
              </div>
              
              <Accordion type="single" collapsible className="w-full">
                {courseModules.map((module, index) => <AccordionItem key={index} value={`item-${index}`} data-index={index}>
                    <AccordionTrigger className="text-base font-semibold hover:text-primary transition-colors">
                      {module.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base pt-2 pb-6">
                      {module.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="rounded-2xl bg-muted/50 p-6 border border-border !w-[700px] sm:p-8">
              <h3 className="text-xl font-semibold text-foreground mb-4">¿Qué lograrás al finalizar?</h3>
              <ul className="space-y-3">
                {["Realizar instalaciones eléctricas domiciliarias completas.", "Interpretar planos y esquemas eléctricos.", "Diagnosticar y reparar fallas en sistemas existentes.", "Trabajar de forma independiente o en empresas del sector."].map((item, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                    <CircleCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 border-border shadow-lg shadow-shadow-color/5 !h-[600px] !w-[350px]">
                <CardHeader className="bg-muted/30 border-b border-border pb-6">
                  <CardTitle className="text-2xl">Resumen del Curso</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Duración</p>
                      <p className="text-base font-semibold text-foreground">6 Meses</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <MonitorPlay className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Modalidad</p>
                      <p className="text-base font-semibold text-foreground">Presencial y Online</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Inversión</p>
                      <p className="text-base font-semibold text-foreground">$18.000</p>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="pt-6 pb-10 px-6 flex flex-col gap-4">
                  <Link to="https://mpago.la/1WfMEZk" className="w-full" newTab>
                    <Button size="lg" className="w-full group text-base font-semibold h-12 bg-[#009EE3] hover:bg-[#008ED2] text-white border-none shadow-md">
                      Pagar con Mercado Pago
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="https://wa.me/59896344395" className="w-full" newTab>
                    <Button size="lg" className="w-full group text-base font-semibold h-12 bg-[#25D366] hover:bg-[#22c35e] text-white border-none shadow-md">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Consultar por WhatsApp
                    </Button>
                  </Link>
                </CardFooter>
                
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}